import React from 'react';
import { Shield, ShieldCheck, ShieldAlert, ShieldX, AlertTriangle } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';

const statusConfig = {
  clear: {
    label: 'Clear',
    icon: ShieldCheck,
    bgClass: 'bg-green-500/20',
    textClass: 'text-green-400',
    borderClass: 'border-green-500/30',
    description: 'No safety concerns'
  },
  under_review: {
    label: 'Under Review',
    icon: Shield,
    bgClass: 'bg-yellow-500/20',
    textClass: 'text-yellow-400',
    borderClass: 'border-yellow-500/30',
    description: 'Account has open reports being reviewed'
  },
  flagged: {
    label: 'Flagged',
    icon: ShieldAlert,
    bgClass: 'bg-orange-500/20',
    textClass: 'text-orange-400',
    borderClass: 'border-orange-500/30',
    description: 'Account flagged for safety concerns'
  },
  suspended: {
    label: 'Suspended',
    icon: ShieldX,
    bgClass: 'bg-red-500/20',
    textClass: 'text-red-400',
    borderClass: 'border-red-500/30',
    description: 'Account suspended due to violations'
  }
};

export const FraudShieldBadge = ({ status = 'clear', size = 'md', showLabel = true }) => {
  const config = statusConfig[status] || statusConfig.clear;
  const Icon = config.icon;
  
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-2.5 py-1',
    lg: 'text-base px-3 py-1.5'
  };
  
  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  };
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={`inline-flex items-center gap-1.5 rounded-full border font-medium ${config.bgClass} ${config.textClass} ${config.borderClass} ${sizeClasses[size]}`}
            data-testid={`fraud-shield-badge-${status}`}
          >
            <Icon className={iconSizes[size]} />
            {showLabel && config.label}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">{config.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const FraudShieldMiniIndicator = ({ status = 'clear' }) => {
  if (status === 'clear') return null;
  
  const config = statusConfig[status] || statusConfig.clear;
  const Icon = config.icon;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={`inline-flex ${config.textClass}`}>
            <Icon className="w-4 h-4" />
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm font-medium">{config.label}</p>
          <p className="text-xs text-gray-400">{config.description}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const TrustSignals = ({ 
  isVerified = false, 
  accountAgeDays = 0, 
  completedLoads = 0, 
  fraudShieldStatus = 'clear',
  compact = false 
}) => {
  if (compact) {
    return (
      <div className="flex items-center gap-2 flex-wrap">
        {isVerified && (
          <span className="inline-flex items-center gap-1 text-xs text-green-400">
            <ShieldCheck className="w-3 h-3" />
            Verified
          </span>
        )}
        {accountAgeDays > 30 && (
          <span className="text-xs text-gray-500">
            {accountAgeDays > 365 ? `${Math.floor(accountAgeDays / 365)}y` : `${accountAgeDays}d`} old
          </span>
        )}
        {completedLoads > 0 && (
          <span className="text-xs text-gray-500">
            {completedLoads} loads
          </span>
        )}
        {fraudShieldStatus !== 'clear' && (
          <FraudShieldMiniIndicator status={fraudShieldStatus} />
        )}
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-3">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className={`flex items-center gap-1 ${isVerified ? 'text-green-400' : 'text-gray-500'}`}>
              <ShieldCheck className="w-4 h-4" />
              <span className="text-xs">{isVerified ? 'Verified' : 'Unverified'}</span>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isVerified ? 'Identity verified by HaulLogix' : 'Identity not yet verified'}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      <div className="text-xs text-gray-500">
        {accountAgeDays} days
      </div>
      
      <div className="text-xs text-gray-500">
        {completedLoads} loads
      </div>
      
      {fraudShieldStatus !== 'clear' && (
        <FraudShieldBadge status={fraudShieldStatus} size="sm" />
      )}
    </div>
  );
};

export default FraudShieldBadge;
